import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  format,
  addDays,
  subDays,
  eachDayOfInterval,
  isSameDay,
  parse,
  startOfWeek,
  endOfWeek,
  parseISO,
  isBefore,
  startOfToday,
  isValid,
} from "date-fns";
import { throttle } from "lodash";
import { Add, Close, CheckCircle } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DATE_FORMATS, ATTENDANCE_STATUS } from "../../../utils/Constants";

const styles = {
  leaveCard: {
    bgcolor: "#FFF3E0",
    color: "#EF6C00",
    borderRadius: "8px",
    border: "1px solid #FFCC80",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  leaveText: { color: "#EF6C00" },
  attendanceCard: (status) => ({
    bgcolor: "#E8F5E9",
    color: "#2E7D32",
    borderRadius: "8px",
    border: "1px solid #A5D6A7",
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    cursor: status === ATTENDANCE_STATUS.ONGOING ? "pointer" : "default",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  }),
  attendanceIcon: { fontSize: 24, color: "#2E7D32" },
  attendanceText: { color: "#1B5E20" },
  shiftCard: (shift, isPast) => {
    const shiftColor = shift.shift?.color || shift.color;
    return {
      bgcolor: shiftColor || "#FFE0B2",
      color: "#fff",
      borderRadius: "8px",
      borderLeft: `5px solid ${shiftColor ? "rgba(0,0,0,0.3)" : "#F57C00"}`,
      p: 1.5,
      width: "100%",
      position: "relative",
      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
      textAlign: "left",
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      cursor: isPast ? "not-allowed" : "grab",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      "&:active": {
        cursor: isPast ? "not-allowed" : "grabbing",
        boxShadow: isPast ? "none" : "0 5px 12px rgba(0,0,0,0.25)",
        transform: isPast ? "none" : "scale(1.02)",
      },
      "&:hover": {
        transform: isPast ? "none" : "translateY(-2px)",
        boxShadow: isPast ? "none" : "0 6px 12px rgba(0,0,0,0.2)",
      },
      "&:hover .delete-action": {
        opacity: 1,
        transform: "translateY(0)",
      },
      "&:hover .resize-handle": {
        opacity: 1,
      },
    };
  },
  resizeHandleLeft: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 16,
    cursor: "w-resize",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    opacity: 0.3,
    transition: "opacity 0.2s",
    "&:hover": { opacity: 1 },
  },
  resizeHandleBarLeft: {
    width: 4,
    height: "40%",
    bgcolor: "rgba(255,255,255,0.7)",
    borderRadius: "0 4px 4px 0",
  },
  resizeHandleRight: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: 16,
    cursor: "e-resize",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    opacity: 0.3,
    transition: "opacity 0.2s",
    "&:hover": { opacity: 1 },
  },
  resizeHandleBarRight: {
    width: 4,
    height: "40%",
    bgcolor: "rgba(255,255,255,0.7)",
    borderRadius: "4px 0 0 4px",
  },
  shiftTimeText: {
    lineHeight: 1.2,
    textShadow: "0 1px 2px rgba(0,0,0,0.1)",
    fontSize: { xs: "0.75rem", sm: "0.875rem" },
    textAlign: "left",
    width: "100%",
  },
  shiftTypeText: {
    opacity: 0.95,
    lineHeight: 1.2,
    mb: 0.5,
    fontSize: { xs: "0.65rem", sm: "0.75rem" },
    textAlign: "left",
    width: "100%",
  },
  dateRangeLabelContainer: {
    bgcolor: "rgba(255,255,255,0.2)",
    px: 0.8,
    py: 0.2,
    borderRadius: "4px",
    alignSelf: "flex-start",
    mt: 0.5,
  },
  dateRangeLabelText: {
    fontWeight: 600,
    fontSize: { xs: "0.6rem", sm: "0.7rem" },
  },
  deleteAction: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 24,
    height: 24,
    bgcolor: "rgba(0,0,0,0.15)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    opacity: 0,
    transform: "translateY(-5px)",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      bgcolor: "#ff5252",
      color: "white",
      transform: "scale(1.1) translateY(0)",
      boxShadow: "0 2px 8px rgba(255, 82, 82, 0.4)",
    },
    zIndex: 20,
  },
  deleteIcon: { fontSize: 16 },
  tableContainer: (dragState) => ({
    height: "70vh",
    border: "1px solid #FFDC8A",
    borderRadius: "12px",
    bgcolor: "#fff",
    overflow: "auto",
    scrollBehavior: "auto",
    cursor: dragState ? "col-resize" : "default",
    userSelect: dragState ? "none" : "auto",
    "&::-webkit-scrollbar": { width: 8, height: 8 },
    "&::-webkit-scrollbar-track": { bgcolor: "#f1f1f1" },
    "&::-webkit-scrollbar-thumb": { bgcolor: "#F7A86E", borderRadius: 4 },
  }),
  table: { minWidth: 800, borderCollapse: "separate" },
  employeeHeaderCell: {
    position: "sticky",
    left: 0,
    zIndex: 3,
    bgcolor: "#FFF8E1",
    minWidth: { xs: 120, sm: 200 },
    borderRight: "1px solid #FFDC8A",
    borderBottom: "1px solid #FFDC8A",
    boxShadow: "2px 0 5px -2px rgba(0,0,0,0.1)",
  },
  employeeHeaderContent: { display: "flex", alignItems: "center", p: 1 },
  employeeHeaderText: { fontSize: { xs: "0.8rem", sm: "1rem" } },
  dateHeaderCell: {
    minWidth: { xs: 100, sm: 150 },
    borderBottom: "1px solid #FFDC8A",
    borderRight: "1px solid #FFDC8A",
    bgcolor: "#FFF8E1",
    color: "#E65100",
  },
  dateHeaderDateText: { fontSize: { xs: "1rem", sm: "1.25rem" } },
  dateHeaderDayText: {
    opacity: 0.8,
    fontSize: { xs: "0.7rem", sm: "0.75rem" },
  },
  dateCell: (isToday, isPast, dragState, isAddEnabled) => ({
    borderRight: "1px solid #FFDC8A",
    borderBottom: "1px solid #FFDC8A",
    bgcolor: isToday ? "#FFFde7" : isPast ? "#fcfcfc" : "inherit",
    p: 0,
    position: "relative",
    height: 80,
    opacity: isPast ? 0.7 : 1,
    cursor: isPast ? "not-allowed" : "default",
    "&:hover .add-icon": {
      opacity: dragState || !isAddEnabled ? 0 : 1,
    },
  }),
  dateCellContent: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  addIconButton: {
    opacity: 0,
    bgcolor: "#FF9800",
    color: "rgba(255, 255, 255, 0.95)",
    width: 32,
    height: 32,
    transition: "opacity 0.1s ease-in-out",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    "&:hover": {
      bgcolor: "#F57C00",
    },
    zIndex: 1,
  },
  addIcon: { fontSize: 20 },
  shiftCell: (isToday) => ({
    borderRight: "1px solid #FFDC8A",
    borderBottom: "1px solid #FFDC8A",
    bgcolor: isToday ? "#FFFde7" : "inherit",
    p: 0,
    position: "relative",
    height: 80,
  }),
  shiftCellContent: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    p: 1,
  },
  employeeRowCell: {
    position: "sticky",
    left: 0,
    zIndex: 2,
    bgcolor: "#fff",
    borderRight: "1px solid #FFDC8A",
    borderBottom: "1px solid #FFDC8A",
    boxShadow: "2px 0 5px -2px rgba(0,0,0,0.1)",
  },
  employeeRowContent: { display: "flex", alignItems: "center", gap: 2 },
  employeeAvatar: { width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } },
  employeeName: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
  menuPaper: {
    mt: 1,
    maxHeight: 300,
    minWidth: 200,
    borderRadius: 2,
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  },
  menuItem: {
    fontSize: "0.9rem",
    fontWeight: 500,
    py: 1.5,
    borderBottom: "1px solid #f0f0f0",
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      bgcolor: "#FFF8E1",
      color: "#F7A86E",
    },
  },
  menuItemContent: { display: "flex", alignItems: "center", gap: 1 },
  menuItemIcon: { fontSize: 18 },
};
const safeFormatTime = (timeStr) => {
  if (!timeStr) return "";
  let d = parse(timeStr, "HH:mm:ss", new Date());
  if (isValid(d)) return format(d, "h:mm a");
  d = parse(timeStr, "h:mm a", new Date());
  if (isValid(d)) return format(d, "h:mm a");
  return timeStr;
};
const CalendarWeekView = ({
  selectedDate,
  employees,
  shifts,
  shiftTemplates = [],
  onAssignShift,
  onDeleteShift,
  onShiftResize,
  onVisibleDateChange,
  onShiftClick,
  renderEventContent,
  enableAddFunc,
  renderAddIcon,
  enableColSpan = true,
}) => {
  const { t } = useTranslation();
  const [visibleEmployees, setVisibleEmployees] = useState([]);
  const [displayDays, setDisplayDays] = useState([]);
  const [visibleLimit, setVisibleLimit] = useState(10);
  const [shouldScrollToCurrent, setShouldScrollToCurrent] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeCell, setActiveCell] = useState({
    employeeId: null,
    date: null,
  });
  const [dragState, setDragState] = useState(null);
  const tableContainerRef = useRef(null);
  const isUpdating = useRef(false);
  const prevScrollWidthRef = useRef(0);
  const isPrependingRef = useRef(false);
  const isScrollingRef = useRef(false);
  console.log("shifts", shifts);
  useEffect(() => {
    if (isScrollingRef.current) {
      isScrollingRef.current = false;
      return;
    }
    const startOfCurrentWeek = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const start = startOfCurrentWeek;
    const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });
    setDisplayDays(days);
    setShouldScrollToCurrent(true);
  }, [selectedDate]);
  useEffect(() => {
    setVisibleEmployees(employees.slice(0, visibleLimit));
  }, [employees, visibleLimit]);
  React.useLayoutEffect(() => {
    const adjustScroll = () => {
      if (!tableContainerRef.current) return;
      if (shouldScrollToCurrent) {
        const currentWeekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const dateStr = format(currentWeekStart, DATE_FORMATS.API_DATE);
        const element = document.getElementById(`header-date-${dateStr}`);
        let stickyWidth = 0;
        if (tableContainerRef.current) {
          const stickyCell = tableContainerRef.current.querySelector("th");
          if (stickyCell)
            stickyWidth = stickyCell.getBoundingClientRect().width;
        }
        if (stickyWidth === 0) stickyWidth = 200;
        if (element) {
          const container = tableContainerRef.current;
          isScrollingRef.current = false;
          container.scrollLeft = element.offsetLeft - stickyWidth;
          setShouldScrollToCurrent(false);
          isPrependingRef.current = false;
        }
      } else if (isPrependingRef.current) {
        const container = tableContainerRef.current;
        const newScrollWidth = container.scrollWidth;
        const diff = newScrollWidth - prevScrollWidthRef.current;
        if (diff > 0) {
          container.scrollLeft += diff;
        }
        isPrependingRef.current = false;
      }
    };
    const timer = setTimeout(adjustScroll, 0);
    return () => clearTimeout(timer);
  }, [displayDays, shouldScrollToCurrent, selectedDate]);
  const addNextDays = useCallback(() => {
    if (isUpdating.current) return;
    isUpdating.current = true;
    setDisplayDays((prev) => {
      const lastDay = prev[prev.length - 1];
      const newDays = eachDayOfInterval({
        start: addDays(lastDay, 1),
        end: addDays(lastDay, 7),
      });
      isUpdating.current = false;
      return [...prev, ...newDays];
    });
  }, []);
  const addPreviousDays = useCallback(() => {
    if (isUpdating.current) return;
    isUpdating.current = true;
    if (tableContainerRef.current) {
      prevScrollWidthRef.current = tableContainerRef.current.scrollWidth;
      isPrependingRef.current = true;
    }
    setDisplayDays((prev) => {
      const firstDay = prev[0];
      const newDays = eachDayOfInterval({
        start: subDays(firstDay, 7),
        end: subDays(firstDay, 1),
      });
      isUpdating.current = false;
      return [...newDays, ...prev];
    });
  }, []);
  const loadMoreEmployees = useCallback(() => {
    setVisibleLimit((prev) => Math.min(prev + 10, employees.length));
  }, [employees.length]);
  const handleScroll = useCallback(
    throttle(() => {
      const container = tableContainerRef.current;
      if (!container) return;
      const {
        scrollTop,
        scrollHeight,
        clientHeight,
        scrollLeft,
        scrollWidth,
        clientWidth,
      } = container;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        if (visibleLimit < employees.length) {
          loadMoreEmployees();
        }
      }
      const threshold = 100;
      if (scrollLeft + clientWidth >= scrollWidth - threshold) {
        addNextDays();
      }
      if (scrollLeft <= threshold) {
        addPreviousDays();
      }
      if (onVisibleDateChange) {
        let stickyWidth = 0;
        const stickyCell = container.querySelector("th");
        if (stickyCell) stickyWidth = stickyCell.getBoundingClientRect().width;
        if (!stickyWidth) stickyWidth = 200;
        const checkX =
          scrollLeft + stickyWidth + (clientWidth - stickyWidth) / 2;

        let foundDate = null;
        for (const date of displayDays) {
          const dateStr = format(date, DATE_FORMATS.API_DATE);
          const el = document.getElementById(`header-date-${dateStr}`);
          if (el) {
            const left = el.offsetLeft;
            const right = left + el.offsetWidth;
            if (checkX >= left && checkX <= right) {
              foundDate = date;
              break;
            }
          }
        }
        if (foundDate) {
          const weekStart = startOfWeek(foundDate, { weekStartsOn: 1 });
          const currentSelectedStart = startOfWeek(selectedDate, {
            weekStartsOn: 1,
          });
          if (weekStart.getTime() !== currentSelectedStart.getTime()) {
            isScrollingRef.current = true;
            onVisibleDateChange(weekStart);
          }
        }
      }
    }, 100),
    [
      visibleLimit,
      employees.length,
      addNextDays,
      addPreviousDays,
      displayDays,
      onVisibleDateChange,
      selectedDate,
    ]
  );
  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);
  const handlePlusClick = (event, employeeId, date) => {
    setMenuAnchor(event.currentTarget);
    setActiveCell({ employeeId, date });
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveCell({ employeeId: null, date: null });
  };
  const handleSelectTemplate = (template) => {
    if (activeCell.employeeId && activeCell.date) {
      onAssignShift({
        ...template,
        employee_id: activeCell.employeeId,
        date: format(activeCell.date, DATE_FORMATS.API_DATE),
        shift_id: template.id,
        created_at: undefined,
        id: undefined,
      });
    }
    handleMenuClose();
  };
  const handleDragStart = (
    e,
    employeeId,
    shift,
    direction,
    startDate,
    endDate
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setDragState({
      employeeId,
      shift,
      direction,
      originalStart: startDate,
      originalEnd: endDate,
      currentStart: startDate,
      currentEnd: endDate,
    });
  };
  const handleDragMove = useCallback(
    (e) => {
      if (!dragState) return;
      const clientX = e.clientX;
      let hoveredDate = null;
      for (const date of displayDays) {
        const dateStr = format(date, DATE_FORMATS.API_DATE);
        const headerId = `header-date-${dateStr}`;
        const headerEl = document.getElementById(headerId);
        if (headerEl) {
          const rect = headerEl.getBoundingClientRect();
          if (clientX >= rect.left && clientX <= rect.right) {
            hoveredDate = date;
            break;
          }
        }
      }
      if (!hoveredDate) return;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (hoveredDate < today) return;
      setDragState((prev) => {
        if (
          prev.direction === "start" &&
          isSameDay(hoveredDate, prev.currentStart)
        )
          return prev;
        if (prev.direction === "end" && isSameDay(hoveredDate, prev.currentEnd))
          return prev;
        let newStart = prev.currentStart;
        let newEnd = prev.currentEnd;
        if (prev.direction === "start") {
          if (hoveredDate > prev.currentEnd) return prev;
          newStart = hoveredDate;
        } else {
          if (hoveredDate < prev.currentStart) return prev;
          newEnd = hoveredDate;
        }
        return { ...prev, currentStart: newStart, currentEnd: newEnd };
      });
    },
    [dragState, displayDays]
  );
  const handleDragEnd = useCallback(() => {
    if (dragState) {
      if (onShiftResize) {
        if (
          dragState.currentStart.getTime() !==
            dragState.originalStart.getTime() ||
          dragState.currentEnd.getTime() !== dragState.originalEnd.getTime()
        ) {
          onShiftResize({
            employeeId: dragState.employeeId,
            shiftType:
              dragState.shift.shift?.shift_type || dragState.shift.shift_type,
            currentStart: dragState.originalStart,
            currentEnd: dragState.originalEnd,
            newStart: dragState.currentStart,
            newEnd: dragState.currentEnd,
            template: dragState.shift,
          });
        }
      }
      setDragState(null);
    }
  }, [dragState, onShiftResize]);
  useEffect(() => {
    if (dragState) {
      window.addEventListener("mousemove", handleDragMove);
      window.addEventListener("mouseup", handleDragEnd);
    }
    return () => {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
    };
  }, [dragState, handleDragMove, handleDragEnd]);
  const isSameShiftType = (s1, s2) => {
    if (!s1 || !s2) return false;
    const type1 = s1.shift?.shift_type || s1.shift_type;
    const type2 = s2.shift?.shift_type || s2.shift_type;
    return type1 === type2;
  };
  const getShiftForDate = (employeeId, date) => {
    if (dragState && dragState.employeeId === employeeId) {
      if (date >= dragState.currentStart && date <= dragState.currentEnd) {
        return {
          ...dragState.shift,
          mock: true,
          employee_id: employeeId,
          date: format(date, DATE_FORMATS.API_DATE),
        };
      }
      const realShift = shifts
        ? shifts.find((s) => {
            let shiftDate = s.date;
            if (typeof s.date === "string") shiftDate = parseISO(s.date);
            return (
              (s.employee_id === employeeId || s.employee_id == employeeId) &&
              isSameDay(shiftDate, date)
            );
          })
        : null;
      const realShiftType =
        realShift?.shift?.shift_type || realShift?.shift_type;
      const dragShiftType =
        dragState.shift?.shift?.shift_type || dragState.shift?.shift_type;
      if (realShift && realShiftType === dragShiftType) {
        const realDate =
          typeof realShift.date === "string"
            ? parseISO(realShift.date)
            : realShift.date;
        if (
          realDate >= dragState.originalStart &&
          realDate <= dragState.originalEnd
        ) {
          return null;
        }
        return realShift;
      }
      return realShift;
    }
    if (!shifts) return null;
    return shifts.find((s) => {
      let shiftDate = s.date;
      if (typeof s.date === "string") shiftDate = parseISO(s.date);
      return (
        (s.employee_id === employeeId || s.employee_id == employeeId) &&
        isSameDay(shiftDate, date)
      );
    });
  };
  const renderDefaultShiftContent = (
    shift,
    employee,
    startDateObj,
    endDateObj,
    dateRangeLabel,
    assignmentIds
  ) => {
    const isPast = isBefore(startDateObj, startOfToday());
    const shiftStart = shift.shift?.start_time || shift.start_time;
    const shiftEnd = shift.shift?.end_time || shift.end_time;
    const shiftType = shift.shift?.shift_type || shift.shift_type;
    const isLeave = shift.status === ATTENDANCE_STATUS.LEAVE;
    const isAttendance =
      shift.status === ATTENDANCE_STATUS.COMPLETED ||
      shift.status === ATTENDANCE_STATUS.ONGOING;
    if (isLeave) {
      return (
        <Box sx={styles.leaveCard}>
          <Typography variant="h4" fontWeight={900} sx={styles.leaveText}>
            L
          </Typography>
        </Box>
      );
    }
    if (isAttendance) {
      return (
        <Box
          onClick={(e) => {
            if (
              shift.status === ATTENDANCE_STATUS.ONGOING &&
              !shift.check_out_time &&
              onShiftClick
            ) {
              onShiftClick(shift);
            }
          }}
          sx={styles.attendanceCard(shift.status)}
        >
          <CheckCircle sx={styles.attendanceIcon} />
          <Typography
            variant="caption"
            fontWeight={600}
            sx={styles.attendanceText}
          >
            {shift.check_in_time && shift.check_out_time
              ? `${safeFormatTime(shift.check_in_time)} - ${safeFormatTime(
                  shift.check_out_time
                )}`
              : `${safeFormatTime(shiftStart)} - ${safeFormatTime(shiftEnd)}`}
          </Typography>
        </Box>
      );
    }
    return (
      <Box
        onClick={(e) => {
          if (onShiftClick) {
            onShiftClick(shift);
          }
        }}
        sx={styles.shiftCard(shift, isPast)}
      >
        {!dragState && !isPast && (
          <>
            <Box
              className="resize-handle"
              onMouseDown={(e) =>
                handleDragStart(
                  e,
                  employee.employee_id,
                  shift,
                  "start",
                  startDateObj,
                  endDateObj
                )
              }
              sx={styles.resizeHandleLeft}
            >
              <Box sx={styles.resizeHandleBarLeft} />
            </Box>
            <Box
              className="resize-handle"
              onMouseDown={(e) =>
                handleDragStart(
                  e,
                  employee.employee_id,
                  shift,
                  "end",
                  startDateObj,
                  endDateObj
                )
              }
              sx={styles.resizeHandleRight}
            >
              <Box sx={styles.resizeHandleBarRight} />
            </Box>
          </>
        )}
        <Typography
          variant="body2"
          display="block"
          fontWeight={700}
          sx={styles.shiftTimeText}
        >
          {safeFormatTime(shiftStart)} - {safeFormatTime(shiftEnd)}
        </Typography>
        <Typography variant="caption" display="block" sx={styles.shiftTypeText}>
          {shiftType}
        </Typography>
        {dateRangeLabel && (
          <Box sx={styles.dateRangeLabelContainer}>
            <Typography
              variant="caption"
              display="block"
              sx={styles.dateRangeLabelText}
            >
              {dateRangeLabel}
            </Typography>
          </Box>
        )}
        {!isPast && (
          <Box
            className="delete-action"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteShift(assignmentIds);
            }}
            sx={styles.deleteAction}
            title={t("common.remove") || "Remove"}
          >
            <Close sx={styles.deleteIcon} />
          </Box>
        )}
      </Box>
    );
  };
  return (
    <TableContainer
      ref={tableContainerRef}
      sx={styles.tableContainer(dragState)}
    >
      <Table stickyHeader sx={styles.table}>
        <TableHead>
          <TableRow>
            <TableCell sx={styles.employeeHeaderCell}>
              <Box sx={styles.employeeHeaderContent}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="primary"
                  sx={styles.employeeHeaderText}
                >
                  {t("pages.shift_management.employee_name") || "Employee Name"}
                </Typography>
              </Box>
            </TableCell>
            {displayDays.map((date) => (
              <TableCell
                key={date.toString()}
                id={`header-date-${format(date, DATE_FORMATS.API_DATE)}`}
                align="center"
                sx={styles.dateHeaderCell}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="inherit"
                  sx={styles.dateHeaderDateText}
                >
                  {format(date, DATE_FORMATS.DISPLAY_DATE)}
                </Typography>
                <Typography
                  variant="caption"
                  color="inherit"
                  sx={styles.dateHeaderDayText}
                >
                  {format(date, DATE_FORMATS.DAY_NAME).toUpperCase()}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleEmployees.map((employee) => {
            const cells = [];
            let i = 0;
            while (i < displayDays.length) {
              const date = displayDays[i];
              const shift = getShiftForDate(employee.employee_id, date);
              const isToday = isSameDay(date, new Date());
              if (!shift) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const isPastDate = date < today;
                let isAddEnabled = !isPastDate;
                if (enableAddFunc) {
                  isAddEnabled = enableAddFunc(date);
                }
                cells.push(
                  <TableCell
                    key={date.toString()}
                    align="center"
                    sx={styles.dateCell(
                      isToday,
                      isPastDate,
                      dragState,
                      isAddEnabled
                    )}
                  >
                    <Box sx={styles.dateCellContent}>
                      {isAddEnabled &&
                        (renderAddIcon ? (
                          renderAddIcon(() =>
                            handlePlusClick(
                              { currentTarget: null },
                              employee.employee_id,
                              date
                            )
                          )
                        ) : (
                          <IconButton
                            className="add-icon"
                            size="small"
                            onClick={(e) =>
                              handlePlusClick(e, employee.employee_id, date)
                            }
                            sx={styles.addIconButton}
                          >
                            <Add sx={styles.addIcon} />
                          </IconButton>
                        ))}
                    </Box>
                  </TableCell>
                );
                i++;
              } else {
                let span = 1;
                const assignmentIds = shift.assignment_id
                  ? [shift.assignment_id]
                  : [];
                const assignmentKeys = shift.assignment_key
                  ? [shift.assignment_key]
                  : [];
                const startDateObj = date;
                const isStartPast = isBefore(startDateObj, startOfToday());
                if (enableColSpan) {
                  while (i + span < displayDays.length) {
                    const nextDate = displayDays[i + span];
                    const nextShift = getShiftForDate(
                      employee.employee_id,
                      nextDate
                    );
                    const isNextPast = isBefore(nextDate, startOfToday());
                    if (isStartPast !== isNextPast) break;

                    if (nextShift && isSameShiftType(shift, nextShift)) {
                      if (nextShift.assignment_id)
                        assignmentIds.push(nextShift.assignment_id);
                      if (nextShift.assignment_key)
                        assignmentKeys.push(nextShift.assignment_key);
                      span++;
                    } else {
                      break;
                    }
                  }
                }
                const endDateObj = displayDays[i + span - 1];
                const dateRangeLabel =
                  span > 1
                    ? `Start Date: ${format(
                        startDateObj,
                        DATE_FORMATS.DISPLAY_DATE
                      )} - End Date: ${format(
                        endDateObj,
                        DATE_FORMATS.DISPLAY_DATE
                      )}`
                    : null;

                let content = null;
                if (renderEventContent) {
                  content = renderEventContent({
                    event: shift,
                    day: date,
                    employee,
                    startDateObj,
                    endDateObj,
                    dateRangeLabel,
                    assignmentIds: assignmentKeys,
                    onDelete: () => onDeleteShift(assignmentKeys),
                    onAssign: (merged) =>
                      onAssignShift({
                        ...merged,
                        date: format(date, DATE_FORMATS.API_DATE),
                      }),
                  });
                }
                if (!content) {
                  content = renderDefaultShiftContent(
                    shift,
                    employee,
                    startDateObj,
                    endDateObj,
                    dateRangeLabel,
                    assignmentKeys
                  );
                }

                cells.push(
                  <TableCell
                    key={date.toString()}
                    align="center"
                    colSpan={span}
                    sx={styles.shiftCell(isToday)}
                  >
                    <Box sx={styles.shiftCellContent}>{content}</Box>
                  </TableCell>
                );
                i += span;
              }
            }
            return (
              <TableRow key={employee.employee_id} hover>
                <TableCell sx={styles.employeeRowCell}>
                  <Box sx={styles.employeeRowContent}>
                    <Avatar
                      src={employee.employee_photo}
                      alt={employee.name}
                      sx={styles.employeeAvatar}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={styles.employeeName}
                    >
                      {employee.name}
                    </Typography>
                  </Box>
                </TableCell>
                {cells}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: styles.menuPaper,
        }}
      >
        {shiftTemplates.map((template, index) => (
          <MenuItem
            key={index}
            onClick={() => handleSelectTemplate(template)}
            sx={styles.menuItem}
          >
            <Box sx={styles.menuItemContent}>
              {template.icon && (
                <Box component="span" sx={styles.menuItemIcon}>
                  {template.icon}
                </Box>
              )}
              <Typography variant="body2">{template.shift_type}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </TableContainer>
  );
};

export default CalendarWeekView;
